import { useState, useEffect, useCallback } from 'react';

interface UseExitIntentOptions {
  threshold?: number; // pixels from top before triggering
  delay?: number; // minimum time on page before enabling (ms)
  sensitivity?: number; // mouse movement sensitivity
}

export const useExitIntent = (options: UseExitIntentOptions = {}) => {
  const {
    threshold = 10,
    delay = 3000, // 3 seconds
    sensitivity = 20
  } = options;

  const [isExiting, setIsExiting] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);

  const handleMouseLeave = useCallback((e: MouseEvent) => {
    // Only trigger if mouse is leaving from the top of the page
    if (e.clientY <= threshold && e.clientX >= 0 && e.clientX <= window.innerWidth) {
      if (isEnabled && !isExiting) {
        setIsExiting(true);
      }
    }
  }, [threshold, isEnabled, isExiting]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    // Track rapid upward mouse movement (another exit intent signal)
    if (e.clientY <= sensitivity && !isExiting && isEnabled) {
      setIsExiting(true);
    }
  }, [sensitivity, isExiting, isEnabled]);

  const reset = useCallback(() => {
    setIsExiting(false);
  }, []);

  useEffect(() => {
    // Enable exit intent detection after delay
    const enableTimer = setTimeout(() => {
      setIsEnabled(true);
    }, delay);

    return () => clearTimeout(enableTimer);
  }, [delay]);

  useEffect(() => {
    if (!isEnabled) return;

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mousemove', handleMouseMove);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isEnabled, handleMouseLeave, handleMouseMove]);

  return { isExiting, reset };
};